import styled, { keyframes } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;

  /* Fullscreen element has no parent card — paint the same shell as normal view. */
  &:fullscreen {
    background: ${rawColors.structural.white};
  }
`;

/**
 * Sits below the filter bar inside Wrapper.
 * Flex-row: [CanvasArea (flex:1)] [KgNodePanel (~30% width, when a node is selected)].
 * Keeps canvas controls fully visible inside CanvasArea — the panel never overlaps them.
 */
export const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  background: ${rawColors.structural.themeLight};
`;

export const CanvasArea = styled.div<{ $isRefining?: boolean }>`
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: ${rawColors.structural.themeLight};
  transition: opacity 0.35s ease;
  opacity: ${({ $isRefining }) => ($isRefining ? 0.9 : 1)};

  .react-flow {
    background: ${rawColors.structural.themeLight};
  }

  .react-flow__handle {
    opacity: 0 !important;
    visibility: hidden !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    pointer-events: none !important;
  }

  /* Icon-view hover cards render above neighboring nodes via NodeToolbar. */
  .react-flow__node {
    overflow: visible !important;
  }

  .react-flow__node:has([data-kg-collapsed-hover]:hover) {
    z-index: 2000 !important;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${rawColors.structural.white};
  border-radius: 16px;
  z-index: 10;
  gap: 12px;
`;

export const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${rawColors.grey[200]};
  border-top-color: ${rawColors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const LoadingLabel = styled.span`
  font-size: 13px;
  color: ${rawColors.text.mediumEmphasis};
`;

export const FetchingOverlay = styled(LoadingOverlay)`
  background: rgba(255, 255, 255, 0.65);
  z-index: 9;
  pointer-events: none;
`;

export const LoadMoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-top: 1px solid ${rawColors.grey[200]};
  flex-shrink: 0;
`;

/** Centered empty state inside the canvas viewport — filters remain usable above. */
export const EmptyStateOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 5;
  pointer-events: none;
`;

export const EmptyStateMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
  max-width: 280px;
`;

export const LoadMoreButton = styled.button`
  font-size: 13px;
  color: ${rawColors.primary[600]};
  background: transparent;
  border: 1px solid ${rawColors.primary[300]};
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${rawColors.primary[50]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
